import { LinkButton } from '#components/atoms/buttons/link-button/LinkButton.tsx';
import { CenteredLayout } from '#components/patterns/layouts/centered/CenteredLayout.tsx';

type Props = {
  addLighthouseHref: string;
  /**
   * Whether managed lighthouses already cover every network. Managed lighthouses are not hosts, so
   * a fully covered account still reaches this empty state with nothing it has to do — the setup
   * guidance would be telling it to close a gap that does not exist.
   */
  isManagedCovered: boolean;
  className?: string;
};

/**
 * {@link EmptyLighthouses} is the empty state for the lighthouse view of the hosts page, shown when
 * the account has no lighthouse hosts and no criteria narrowing the view. It renders inside the
 * table area so the header - search, filter, and the Type pill that got the user here - stays
 * available; coverage gaps are the page-level NetworkCoverageBanner's job.
 *
 * Managed lighthouses never appear in this list, so the state covers two different accounts: one
 * with no lighthouse coverage at all, which needs the setup guidance, and one whose networks are
 * fully covered by managed lighthouses, for which adding a self-hosted lighthouse is optional.
 */
export function EmptyLighthouses({ addLighthouseHref, isManagedCovered, className }: Props) {
  if (isManagedCovered) {
    return (
      <CenteredLayout className={className}>
        <div>
          <h2 className="h3">No self-hosted lighthouses</h2>
          <p className="p-lt mb-24">
            Managed lighthouses keep your networks connected, so there’s nothing you need to set up here. You can still
            add your own for more control over how hosts connect — read{' '}
            <a
              className="a"
              href="https://www.defined.net/docs/get-started/enrolling-a-lighthouse/"
              target="_blank"
              rel="help"
            >
              Enrolling a Lighthouse
            </a>
            .
          </p>
          <LinkButton to={addLighthouseHref}>Add lighthouse</LinkButton>
        </div>
      </CenteredLayout>
    );
  }

  return (
    <CenteredLayout className={className}>
      <div>
        <h2 className="h3">No lighthouses set up yet</h2>
        <p className="p-lt">
          Lighthouses assist in establishing direct connections between hosts and keep track of potential routes to each
          host.
        </p>
        <ul className="ul-lt">
          <li>Each overlay network needs at least one lighthouse.</li>
          <li>
            If you plan to access hosts over the Internet, at least one lighthouse will need a static, public IPv4
            address with its firewall configured to allow inbound udp traffic on a port.
          </li>
          <li>A modestly-sized cloud instance should be sufficient for most users.</li>
        </ul>
        <p className="p-lt mb-24">
          For more information, read{' '}
          <a
            className="a"
            href="https://www.defined.net/docs/get-started/enrolling-a-lighthouse/"
            target="_blank"
            rel="help"
          >
            Enrolling a Lighthouse
          </a>
          .
        </p>
        <LinkButton to={addLighthouseHref}>Add lighthouse</LinkButton>
      </div>
    </CenteredLayout>
  );
}
